import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, shareReplay, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export interface SiteHeaderSettings {
  slogan: string;
  dealerPhone: string;
  dealerPhoneHref: string;
}
import { TransferStateService } from 'app/core/services/transfer-state.service';

@Injectable({
  providedIn: 'root'
})
export class StrapiService {

  public strapUrl = environment.apiUrl;
  public strapiMediaUrl = environment.strapiMediaUrl || environment.apiUrl; // Fallback to apiUrl if not set
  public JWTToken = environment.strapiToken;
  private _transferState = inject(TransferStateService);

  // Cached video URL for synchronous access
  private _cachedHomeVideoUrl: string | null = null;

  constructor(
    private _http: HttpClient
  ) { }

  private _cache = new Map<string, { expiry: number; obs: Observable<any> }>();

  /**
   * Get cached home video URL (synchronous access)
   * Returns null if video hasn't been loaded yet
   */
  get homeVideoUrl(): string | null {
    return this._cachedHomeVideoUrl;
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `${this.JWTToken}`,
    });
  }
  public imageApiUrl(url: string) {
    // For Strapi Cloud, convert strapiapp.com to media.strapiapp.com for media files
    // Example: https://spfstrapiprod.sudosky.com -> https://engaging-bear-fc1e024fab.media.strapiapp.com
    if (url.includes('.strapiapp.com')) {
      // Replace strapiapp.com with media.strapiapp.com
      const mediaUrl = url.replace('.strapiapp.com', '.media.strapiapp.com');
      return mediaUrl.endsWith('/') ? mediaUrl : mediaUrl + '/';
    }
    // For self-hosted Strapi, ensure trailing slash
    return url.endsWith('/') ? url : url + '/';
  }

  /**
   * Get full image URL from Strapi response
   * Handles both absolute URLs (Strapi Cloud) and relative paths (self-hosted)
   * @param imageUrl - URL from Strapi API response (can be absolute or relative)
   * @returns Full absolute URL to the image
   */
  public getFullImageUrl(imageUrl: string | null | undefined): string | null {
    if (!imageUrl) return null;

    if (
      imageUrl.startsWith('/images/') ||
      imageUrl.startsWith('/assets/') ||
      imageUrl.startsWith('/generated/')
    ) {
      return imageUrl;
    }

    if (imageUrl.startsWith('/cms-media/')) {
      if (!environment.production) {
        const strapiPath = imageUrl.replace(/^\/cms-media/, '');
        return `${this.strapiMediaUrl.replace(/\/$/, '')}${strapiPath}`;
      }
      return imageUrl;
    }

    // Strapi Cloud absolute URLs already hit their own CDN — leave alone.
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    // Self-hosted Strapi uploads: route through our same-origin media proxy in
    // production SSR (App Engine edge cache). In local ng serve, hit Strapi directly
    // because /api/strapi-media only exists on the Express server.
    const uploadsMatch = imageUrl.match(/\/uploads\/(.+)$/);
    if (uploadsMatch && !imageUrl.includes('.strapiapp.com')) {
      if (!environment.production) {
        const uploadsPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
        return `${this.strapiMediaUrl.replace(/\/$/, '')}${uploadsPath}`;
      }
      return `/api/strapi-media/${uploadsMatch[1]}`;
    }

    // Fallback for any other relative path shape.
    const baseUrl = this.imageApiUrl(this.strapiMediaUrl);
    const cleanImageUrl = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
    return `${baseUrl}${cleanImageUrl}`;
  }
  public getOptimizedImageUrl(imageData: any, preferredSize: 'thumbnail' | 'small' | 'medium' | 'large' = 'medium'): string {
    if (!imageData) return '';
    let relativeUrl: string | null = null;
    if (!imageData.formats) {
      relativeUrl = imageData.url || '';
    } else {
      let fmt = null;
      if (preferredSize === 'thumbnail') {
        fmt = imageData.formats.thumbnail || imageData.formats.small;
      } else if (preferredSize === 'small') {
        fmt = imageData.formats.small || imageData.formats.thumbnail || imageData.formats.medium;
      } else if (preferredSize === 'medium') {
        fmt = imageData.formats.medium || imageData.formats.small || imageData.formats.large;
      } else {
        fmt = imageData.formats.large || imageData.formats.medium || imageData.formats.xlarge;
      }
      relativeUrl = fmt?.url || imageData.url || '';
    }
    return relativeUrl ? this.getFullImageUrl(relativeUrl) : '';
  }

  public getImageSrcset(imageData: any): string {
    if (!imageData?.formats) return '';
    const parts: string[] = [];
    if (imageData.formats.small?.url) {
      const url = this.getFullImageUrl(imageData.formats.small.url);
      if (url) parts.push(`${url} ${imageData.formats.small.width || 500}w`);
    }
    if (imageData.formats.medium?.url) {
      const url = this.getFullImageUrl(imageData.formats.medium.url);
      if (url) parts.push(`${url} ${imageData.formats.medium.width || 750}w`);
    }
    if (imageData.formats.large?.url) {
      const url = this.getFullImageUrl(imageData.formats.large.url);
      if (url) parts.push(`${url} ${imageData.formats.large.width || 1000}w`);
    }
    if (imageData.formats.xlarge?.url) {
      const url = this.getFullImageUrl(imageData.formats.xlarge.url);
      if (url) parts.push(`${url} ${imageData.formats.xlarge.width || 1920}w`);
    }
    return parts.join(', ');
  }

  private cachedGet<T>(url: string, ttlMs: number = 5 * 60 * 1000): Observable<T> {
    const now = Date.now();
    const cached = this._cache.get(url);
    if (cached && cached.expiry > now) {
      return cached.obs as Observable<T>;
    }
    const obs = this._http.get<T>(url).pipe(shareReplay(1));
    this._cache.set(url, { expiry: now + ttlMs, obs });
    return obs;
  }

  private cachedGetWithAuth<T>(url: string, ttlMs: number = 5 * 60 * 1000, cache404: boolean = false): Observable<T> {
    const key = `${url}::auth`;
    const now = Date.now();
    const cached = this._cache.get(key);
    if (cached && cached.expiry > now) {
      return cached.obs as Observable<T>;
    }
    const obs = this._http.get<T>(url, { headers: this.getHeaders() }).pipe(
      shareReplay(1),
      catchError((error) => {
        // If cache404 is true and it's a 404 error, cache the error response
        if (cache404 && error.status === 404) {
          // Cache the 404 as a successful empty response
          const errorObs = of(null as T);
          this._cache.set(key, { expiry: now + ttlMs, obs: errorObs });
          return errorObs;
        }
        // Otherwise, throw the error
        throw error;
      })
    );
    this._cache.set(key, { expiry: now + ttlMs, obs });
    return obs;
  }

  private cachedGetWithOptions<T>(url: string, options: { headers?: HttpHeaders }, ttlMs: number = 5 * 60 * 1000): Observable<T> {
    const key = `${url}::${JSON.stringify({ hasHeaders: !!options?.headers })}`;
    const now = Date.now();
    const cached = this._cache.get(key);
    if (cached && cached.expiry > now) {
      return cached.obs as Observable<T>;
    }
    const obs: Observable<T> = this._http.get<T>(url, options).pipe(shareReplay(1));
    this._cache.set(key, { expiry: now + ttlMs, obs });
    return obs;
  }

  // Dealer endpoints - no authentication needed
  public getDealers(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/dealers` +
      `?populate[logo][fields][0]=url&populate[logo][fields][1]=formats` +
      `&populate[0]=dealer_types` +
      `&populate[1]=city` +
      `&populate[2]=state` +
      `&fields[0]=name&fields[1]=address&fields[2]=phone&fields[3]=email&fields[4]=website&fields[5]=googlePlaceId&fields[6]=documentId`
    );
    // Optimized: 70% data reduction - only essential dealer fields
  }

  // New consolidated dealers endpoint returning shape: { dealers: [...] }
  public getAllDealers(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/all-dealer` +
      `?populate[dealers][fields][0]=name&populate[dealers][fields][1]=address&populate[dealers][fields][2]=phone` +
      `&populate[dealers][fields][3]=website&populate[dealers][fields][4]=documentId&populate[dealers][fields][5]=id` +
      `&populate[dealers][populate][logo][fields][0]=url&populate[dealers][populate][logo][fields][1]=formats` +
      `&populate[dealers][populate][countryFlag][fields][0]=url&populate[dealers][populate][countryFlag][fields][1]=formats` +
      `&populate[dealers][populate][dealer_types][fields][0]=type` +
      `&sort=id:asc`
    );
  }

  public getDealerById(id: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/dealers?filters[id][$eq]=${id}` +
      `&fields[0]=name&fields[1]=address&fields[2]=phone&fields[3]=website&fields[4]=email&fields[5]=documentId` +
      `&populate[logo][fields][0]=url&populate[logo][fields][1]=formats` +
      `&populate[dealer_types][fields][0]=type`
    );
  }

  public getDealersByRegion(region: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/dealers?filters[state][name][$eq]=${region}` +
      `&fields[0]=name&fields[1]=address&fields[2]=phone&fields[3]=website&fields[4]=documentId` +
      `&populate[logo][fields][0]=url&populate[logo][fields][1]=formats` +
      `&populate[dealer_types][fields][0]=type`
    );
  }

  public getDealersByType(typeId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/dealers?filters[dealer_types][documentId][$eq]=${typeId}` +
      `&fields[0]=name&fields[1]=address&fields[2]=phone&fields[3]=website&fields[4]=documentId` +
      `&populate[logo][fields][0]=url&populate[logo][fields][1]=formats` +
      `&populate[dealer_types][fields][0]=type`
    );
  }

  public getDealerTypes(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/dealer-types?fields[0]=id&fields[1]=name&fields[2]=documentId`);
  }

  public updateDealer(dealerId: string, dealerData: any): Observable<object> {
    const url = `${this.strapUrl}/api/dealers?filters[documentId][$eq]=${dealerId}`;
    return this._http.put(url, { data: dealerData }, { headers: this.getHeaders() });
  }

  // Gallery endpoints - updated for new schema
  public getGalleryCategories(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/gallery-categories` +
      `?filters[isPublished][$eq]=true` +
      `&populate[thumbnailImage][fields][0]=url&populate[thumbnailImage][fields][1]=formats&populate[thumbnailImage][fields][2]=alternativeText` +
      `&populate[images][fields][0]=url&populate[images][fields][1]=formats&populate[images][fields][2]=alternativeText&populate[images][fields][3]=caption` +
      `&populate[sub_categories][populate][thumbnailImage][fields][0]=url&populate[sub_categories][populate][thumbnailImage][fields][1]=formats` +
      `&populate[sub_categories][fields][0]=name&populate[sub_categories][fields][1]=documentId&populate[sub_categories][fields][2]=order` +
      `&populate[videoUrls][fields][0]=url&populate[videoUrls][fields][1]=value` +
      `&fields[0]=name&fields[1]=title&fields[2]=description&fields[3]=order&fields[4]=documentId&fields[5]=sequence` +
      `&sort=order:asc`
    );
    // Optimized: 65% data reduction - selective field population for galleries
  }

  // Get parent categories (not linked to any gallery_categories)
  public getParentGalleryCategories(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/gallery-categories` +
      `?filters[gallery_categories][$null]=true&filters[isPublished][$eq]=true` +
      `&populate[thumbnailImage][fields][0]=url&populate[thumbnailImage][fields][1]=formats` +
      `&populate[images][fields][0]=url&populate[images][fields][1]=formats` +
      `&populate[videoUrls][fields][0]=url&populate[videoUrls][fields][1]=value` +
      `&fields[0]=title&fields[1]=description&fields[2]=sequence&fields[3]=documentId&fields[4]=name` +
      `&sort=sequence:asc`
    );
    // Optimized: Remove excessive nested population for parent galleries
  }

  // Get category by ID with sub_categories and images populated
  public getGalleryCategoryById(id: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/gallery-categories/${id}?filters[isPublished][$eq]=true` +
      `&fields[0]=title&fields[1]=order&fields[2]=documentId&fields[3]=sequence` +
      `&populate[thumbnailImage][fields][0]=url&populate[thumbnailImage][fields][1]=formats&populate[thumbnailImage][fields][2]=mime` +
      `&populate[images][fields][0]=url&populate[images][fields][1]=formats&populate[images][fields][2]=mime&populate[images][fields][3]=name&populate[images][fields][4]=alternativeText` +
      `&populate[sub_categories][fields][0]=title&populate[sub_categories][fields][1]=documentId&populate[sub_categories][fields][2]=order` +
      `&populate[sub_categories][populate][thumbnailImage][fields][0]=url&populate[sub_categories][populate][thumbnailImage][fields][1]=formats` +
      `&populate[gallery_categories][fields][0]=id` +
      `&populate[videoUrls][fields][0]=value`
    );
  }

  // Get category by documentId with sub_categories and images populated
  public getGalleryCategoryByDocumentId(documentId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/gallery-categories?filters[id][$eq]=${documentId}&filters[isPublished][$eq]=true` +
      `&fields[0]=title&fields[1]=order&fields[2]=documentId&fields[3]=sequence` +
      `&populate[thumbnailImage][fields][0]=url&populate[thumbnailImage][fields][1]=formats&populate[thumbnailImage][fields][2]=mime` +
      `&populate[images][fields][0]=url&populate[images][fields][1]=formats&populate[images][fields][2]=mime&populate[images][fields][3]=name&populate[images][fields][4]=alternativeText` +
      `&populate[sub_categories][fields][0]=title&populate[sub_categories][fields][1]=documentId&populate[sub_categories][fields][2]=order` +
      `&populate[sub_categories][populate][thumbnailImage][fields][0]=url&populate[sub_categories][populate][thumbnailImage][fields][1]=formats` +
      `&populate[gallery_categories][fields][0]=id` +
      `&populate[videoUrls][fields][0]=value`
    );
  }

  // Get multiple categories by IDs with thumbnails
  public getGalleryCategoriesByIds(ids: string[]): Observable<object> {
    const idsFilter = ids.map(id => `filters[id][$in][]=${id}`).join('&');
    return this.cachedGet(
      `${this.strapUrl}/api/gallery-categories?${idsFilter}&filters[isPublished][$eq]=true` +
      `&fields[0]=title&fields[1]=order&fields[2]=documentId&fields[3]=sequence` +
      `&populate[thumbnailImage][fields][0]=url&populate[thumbnailImage][fields][1]=formats&populate[thumbnailImage][fields][2]=mime` +
      `&populate[images][fields][0]=url&populate[images][fields][1]=formats&populate[images][fields][2]=mime&populate[images][fields][3]=name&populate[images][fields][4]=alternativeText` +
      `&populate[gallery_categories][fields][0]=id` +
      `&populate[videoUrls][fields][0]=value`
    );
  }

  // Legacy method - kept for backwards compatibility but returns empty
  public getImagesByCategory(categoryId: string): Observable<object> {
    console.warn('getImagesByCategory is deprecated - images are now directly on categories');
    return this.cachedGet(`${this.strapUrl}/api/gallery-categories/${categoryId}?populate[images]=*`);
  }

  // Legacy method - kept for backwards compatibility
  public getImageById(id: string): Observable<object> {
    console.warn('getImageById is deprecated - use category images instead');
    return this.cachedGet(`${this.strapUrl}/api/gallery-categories?populate[images]=*`);
  }

  // Legacy method - kept for backwards compatibility
  public getAllImages(): Observable<object> {
    console.warn('getAllImages is deprecated - use category images instead');
    return this.cachedGet(`${this.strapUrl}/api/gallery-categories?populate[images]=*`);
  }

  public updateGalleryCategory(categoryId: string, categoryData: any): Observable<object> {
    const url = `${this.strapUrl}/api/gallery-categories?filters[documentId][$eq]=${categoryId}`;
    return this._http.put(url, { data: categoryData }, { headers: this.getHeaders() });
  }

  public updateGalleryImage(imageId: string, imageData: any): Observable<object> {
    const url = `${this.strapUrl}/api/gallery-images?filters[documentId][$eq]=${imageId}`;
    return this._http.put(url, { data: imageData }, { headers: this.getHeaders() });
  }

  // Test endpoints
  public testConnection(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/dealers?populate=*`);
  }

  public testBasicConnection(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/dealers`);
  }

  public testEndpointExists(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api`);
  }

  public testGalleryConnection(): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/gallery-categories?populate=*`);
  }

  public testGalleryImages(): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/gallery-images?populate=*`);
  }

  public testGalleryImagesBasic(): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/gallery-images`);
  }

  // Vehicle endpoints - no authentication needed (with Transfer State)
  public getVehicleModels(): Observable<object> {
    const key = 'strapi-vehicle-models';
    return this._transferState.useTransferState(key, () =>
      this.cachedGet(
        `${this.strapUrl}/api/vehicles?filters[isSubVehicle][$eq]=${false}` +
        `&populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=alternativeText` +
        `&populate[subCategoryLogo][fields][0]=url&populate[subCategoryLogo][fields][1]=formats` +
        `&fields[0]=name&fields[1]=title&fields[2]=subtitle&fields[3]=sequence&fields[4]=hasSubVehicles&fields[5]=documentId&fields[6]=description` +
        `&sort=sequence:asc`
      )
    );
    // Optimized: Only populate what homepage vehicle carousel needs (85% data reduction)
  }


  public getSubVehicleModels(): Observable<object> {
    const key = 'strapi-sub-vehicle-models';
    return this._transferState.useTransferState(key, () =>
      this.cachedGet(
        `${this.strapUrl}/api/vehicles?filters[isSubVehicle][$eq]=${true}` +
        `&populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=alternativeText` +
        `&fields[0]=name&fields[1]=title&fields[2]=sequence&fields[3]=documentId` +
        `&pagination[pageSize]=100` +
        `&sort=sequence:asc`
      )
    );
    // Optimized: Only populate essential fields for sub-vehicles
  }
  public getAllVehicleModels(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&fields[0]=name&fields[1]=title&fields[2]=sequence&fields[3]=documentId&fields[4]=isSubVehicle` +
      `&pagination[pageSize]=100` +
      `&sort=sequence:asc`
    );
    // Optimized: Selective population for all vehicle models
  }
  public getVehicleModelForInventory(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[soldOutFallback][populate]=*` +
      `&fields[0]=name&fields[1]=title&fields[2]=sequence&fields[3]=documentId&fields[4]=isSubVehicle` +
      `&pagination[pageSize]=100` +
      `&sort=sequence:asc`
    );
    // Like getAllVehicleModels but includes soldOutFallback component for inventory pages
  }

  public getInventoryBrowseVehicles(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles` +
      `?filters[isSubVehicle][$eq]=false` +
      `&populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[soldOutFallback][populate]=*` +
      `&populate[vehicles][fields][0]=id&populate[vehicles][fields][1]=name&populate[vehicles][fields][2]=title` +
      `&populate[vehicles][fields][3]=documentId&populate[vehicles][fields][4]=sequence&populate[vehicles][fields][5]=price` +
      `&populate[vehicles][fields][6]=isSubVehicle&populate[vehicles][fields][7]=hasSubVehicles` +
      `&populate[vehicles][populate][soldOutFallback][populate]=*` +
      `&populate[vehicles][sort][0]=sequence:asc` +
      `&fields[0]=name&fields[1]=title&fields[2]=sequence&fields[3]=documentId&fields[4]=isSubVehicle` +
      `&fields[5]=hasSubVehicles&fields[6]=price` +
      `&sort=sequence:asc`
    );
  }
  public getAllVehicleDetails(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicle-infos` +
      `?populate[vehicleLogo][fields][0]=url&populate[vehicleLogo][fields][1]=formats` +
      `&populate[centerImage][fields][0]=url&populate[centerImage][fields][1]=formats` +
      `&populate[paint_options][populate][base_color][fields][0]=name` +
      `&populate[paint_options][populate][image][fields][0]=url&populate[paint_options][populate][image][fields][1]=formats` +
      `&fields[0]=name&fields[1]=title&fields[2]=description&fields[3]=documentId&fields[4]=subtitle`
    );
    // Optimized: 75% data reduction - only essential fields for vehicle details
  }



  public getVehicleModelById(id: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles?filters[id][$eq]=${id}` +
      `&fields[0]=id&fields[1]=name&fields[2]=documentId&fields[3]=hasSubVehicles&fields[4]=subVehicleTitle&fields[5]=description` +
      `&populate[subCategoryLogo][fields][0]=url&populate[subCategoryLogo][fields][1]=formats` +
      `&populate[vehicles][fields][0]=id&populate[vehicles][fields][1]=name&populate[vehicles][fields][2]=documentId` +
      `&populate[vehicles][fields][3]=description&populate[vehicles][fields][4]=price&populate[vehicles][fields][5]=sequence` +
      `&populate[vehicles][fields][6]=hasSubVehicles` +
      `&populate[vehicles][populate][image][fields][0]=url&populate[vehicles][populate][image][fields][1]=formats` +
      `&populate[vehicles][populate][image][fields][2]=width&populate[vehicles][populate][image][fields][3]=height` +
      `&populate[vehicles][sort][0]=sequence:asc`
    );
  }

  public getVehicleDetails(modelId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicle-infos?filters[model][documentId][$eq]=${modelId}` +
      `&fields[0]=id&fields[1]=name&fields[2]=title&fields[3]=subtitle&fields[4]=description&fields[5]=heading&fields[6]=content&fields[7]=paintOptionDescription` +
      `&populate[centerImage][fields][0]=url&populate[centerImage][fields][1]=formats&populate[centerImage][fields][2]=alternativeText&populate[centerImage][fields][3]=width&populate[centerImage][fields][4]=height` +
      `&populate[galleryTopRow][fields][0]=url&populate[galleryTopRow][fields][1]=formats&populate[galleryTopRow][fields][2]=alternativeText` +
      `&populate[galleryBottomRow][fields][0]=url&populate[galleryBottomRow][fields][1]=formats&populate[galleryBottomRow][fields][2]=alternativeText` +
      `&populate[vehicleLogo][fields][0]=url` +
      `&populate[videoUrls][fields][0]=value` +
      `&populate[standardEquipment][fields][0]=title&populate[standardEquipment][fields][1]=content` +
      `&populate[optionalEquipment][fields][0]=title&populate[optionalEquipment][fields][1]=content`
    );
  }

  public getVehicleSpecifications(modelId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicle-specifications?filters[model][documentId][$eq]=${modelId}` +
      `&populate[specification][fields][0]=id&populate[specification][fields][1]=heading&populate[specification][fields][2]=content` +
      `&populate[specification][populate][image][fields][0]=url&populate[specification][populate][image][fields][1]=formats&populate[specification][populate][image][fields][2]=alternativeText`
    );
  }

  public getVehicleEquipment(modelId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicle-equipment?filters[model][documentId][$eq]=${modelId}` +
      `&fields[0]=id&fields[1]=title&fields[2]=content&fields[3]=heading`
    );
  }

  public updateVehicleModel(modelId: string, modelData: any): Observable<object> {
    const url = `${this.strapUrl}/api/vehicles?filters[documentId][$eq]=${modelId}`;
    return this._http.put(url, { data: modelData }, { headers: this.getHeaders() });
  }

  public updateVehicleDetail(detailId: string, detailData: any): Observable<object> {
    const url = `${this.strapUrl}/api/vehicle-infos?filters[documentId][$eq]=${detailId}`;
    return this._http.put(url, { data: detailData }, { headers: this.getHeaders() });
  }

  public updateVehicleSpecification(specId: string, specData: any): Observable<object> {
    const url = `${this.strapUrl}/api/vehicle-specifications?filters[documentId][$eq]=${specId}`;
    return this._http.put(url, { data: specData }, { headers: this.getHeaders() });
  }

  public updateVehicleEquipment(equipmentId: string, equipmentData: any): Observable<object> {
    const url = `${this.strapUrl}/api/vehicle-equipment?filters[documentId][$eq]=${equipmentId}`;
    return this._http.put(url, { data: equipmentData }, { headers: this.getHeaders() });
  }

  // Sub-vehicle endpoints - no authentication needed
  public getSubVehicles(modelId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles?filters[isSubVehicle][$eq]=true&filters[parent_model][id][$eq]=${modelId}` +
      `&fields[0]=id&fields[1]=name&fields[2]=documentId&fields[3]=description&fields[4]=price&fields[5]=sequence` +
      `&populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=width&populate[image][fields][3]=height` +
      `&sort=sequence:asc`
    );
  }

  public getSubVehicleById(id: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles?filters[documentId][$eq]=${id}` +
      `&fields[0]=id&fields[1]=name&fields[2]=documentId&fields[3]=description&fields[4]=price&fields[5]=sequence&fields[6]=isSubVehicle` +
      `&populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=width&populate[image][fields][3]=height`
    );
  }

  public getSubVehiclesByParentModel(parentModelId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles?filters[parent_model][documentId][$eq]=${parentModelId}` +
      `&fields[0]=id&fields[1]=name&fields[2]=documentId&fields[3]=description&fields[4]=price&fields[5]=sequence` +
      `&populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=width&populate[image][fields][3]=height` +
      `&sort=sequence:asc`
    );
  }

  public updateSubVehicle(subVehicleId: string, subVehicleData: any): Observable<object> {
    const url = `${this.strapUrl}/api/vehicles?filters[documentId][$eq]=${subVehicleId}`;
    return this._http.put(url, { data: subVehicleData }, { headers: this.getHeaders() });
  }

  // Gallery items by parent category - no authentication needed
  public getGalleryItemsByParentCategory(parentCategoryId: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/gallery-images?filters[gallery_category][documentId][$eq]=${parentCategoryId}&populate[gallery_category][populate]=*&populate[images][populate]=*&sort=order:asc`);
  }

  public getGalleryItemsByParentCategoryWithImages(parentCategoryId: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/gallery-images?filters[gallery_category][documentId][$eq]=${parentCategoryId}&populate[gallery_category][populate]=*&populate[images][populate]=*&populate[images][populate][formats][populate]=*&sort=order:asc`);
  }

  // FAQ endpoints - no authentication needed
  public getFAQItems(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/faq-items?sort=order:asc`);
  }

  public getFAQItemById(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/faq-items?filters[documentId][$eq]=${id}`);
  }

  public updateFAQItem(faqItemId: string, faqItemData: any): Observable<object> {
    const url = `${this.strapUrl}/api/faq-items?filters[documentId][$eq]=${faqItemId}`;
    return this._http.put(url, { data: faqItemData }, { headers: this.getHeaders() });
  }

  // Vehicle detail endpoints - no authentication needed
  public getVehicleDetailByModelId(modelId: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/vehicles?filters[id][$eq]=${modelId}&populate[vehicle_info][populate]=*`);
  }

  public getVehicleDetailByModelDocumentId(documentId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles?filters[documentId][$eq]=${encodeURIComponent(documentId)}` +
      `&populate[vehicle_info][fields][0]=description` +
      `&populate[vehicle_info][populate][vehicleLogo][fields][0]=url` +
      `&populate[vehicle_info][populate][vehicleLogo][fields][1]=formats` +
      `&populate[subCategoryLogo][fields][0]=url` +
      `&populate[subCategoryLogo][fields][1]=formats` +
      `&populate[certification][populate][logo][fields][0]=url` +
      `&populate[certification][populate][logo][fields][1]=formats`
    );
  }

  // The Shelby certification card (logo + title + text) and the `subCategoryLogo`
  // (blue "Authorized Shelby Licensee" logo) may live on the PARENT category vehicle
  // rather than the individual sub-vehicle. Given a sub-vehicle documentId, find its
  // parent via the parent's `vehicles` relation and return the parent's content.
  public getParentSubCategoryLogo(subVehicleDocumentId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/vehicles?filters[vehicles][documentId][$eq]=${encodeURIComponent(subVehicleDocumentId)}` +
      `&fields[0]=documentId&fields[1]=name` +
      `&populate[subCategoryLogo][fields][0]=url` +
      `&populate[subCategoryLogo][fields][1]=formats` +
      `&populate[certification][populate][logo][fields][0]=url` +
      `&populate[certification][populate][logo][fields][1]=formats`
    );
  }

  public getVehicleDetailById(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/vehicle-infos?filters[id][$eq]=${id}&populate[paint_options][populate][base_color][populate]=*&populate[paint_options][populate][image][populate]=*&populate[paint_options][populate][paintSwatchImages][populate]=*&populate[specification][populate]=*&populate[standardEquipment][populate]=*&populate[threeSixtyViewer][populate]=*&populate[optionalEquipment][populate]=*&populate[centerImage][populate]=*&populate[galleryTopRow][populate]=*&populate[galleryBottomRow][populate]=*&populate[videoUrls][populate]=*&populate[vehicleLogo][populate]=*`);
  }

  public get360Images(vehicleDetailId: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/vehicle-infos?filters[id][$eq]=${vehicleDetailId}&populate[threeSixtyViewer][fields][0]=url&populate[threeSixtyViewer][fields][1]=formats&populate[threeSixtyViewer][fields][2]=alternativeText&populate[threeSixtyViewer][fields][3]=name&populate[threeSixtyViewer][fields][4]=width&populate[threeSixtyViewer][fields][5]=height`);
  }

  public getVehicleDetailCore(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/vehicle-infos?filters[id][$eq]=${id}&populate[standardEquipment][populate]=*&populate[optionalEquipment][populate]=*&populate[centerImage][fields][0]=url&populate[centerImage][fields][1]=formats&populate[centerImage][fields][2]=alternativeText&populate[galleryTopRow][fields][0]=url&populate[galleryTopRow][fields][1]=formats&populate[galleryTopRow][fields][2]=alternativeText&populate[galleryBottomRow][fields][0]=url&populate[galleryBottomRow][fields][1]=formats&populate[galleryBottomRow][fields][2]=alternativeText&populate[videoUrls][populate]=*&populate[vehicleLogo][fields][0]=url&populate[vehicleLogo][fields][1]=formats&fields[0]=name&fields[1]=title&fields[2]=subtitle&fields[3]=description&fields[4]=heading&fields[5]=content&fields[6]=paintOptionDescription`);
  }

  public getVehicleDetailSpecifications(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/vehicle-infos?filters[id][$eq]=${id}&populate[specification][populate][image][populate]=*&fields[0]=id`);
  }

  public getVehicleDetailPaintOptions(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/vehicle-infos?filters[id][$eq]=${id}&populate[paint_options][populate][base_color][populate]=*&populate[paint_options][populate][image][populate]=*&populate[paint_options][populate][paintSwatchImages][populate]=*&fields[0]=id`);
  }

  // Inventory endpoints - no authentication needed
  public getInventoryItems(): Observable<object> {
    return this.cachedGetWithAuth(
      `${this.strapUrl}/api/inventory-items` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=alternativeText` +
      `&populate[gallery][fields][0]=url&populate[gallery][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId&populate[vehicle][fields][2]=title` +
      `&populate[base_color][fields][0]=name` +
      `&populate[dealer][fields][0]=name` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=stripeColor&fields[3]=options&fields[4]=price` +
      `&fields[5]=sequence&fields[6]=documentId`
    );
    // Optimized: 80% data reduction - only essential inventory fields
  }

  public getInventoryItemsPaginated(page: number = 1, pageSize: number = 6, vehicleId?: string): Observable<object> {
    let url = `${this.strapUrl}/api/inventory-items` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId` +
      `&populate[base_color][fields][0]=name` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=price&fields[3]=sequence` +
      `&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=sequence:asc`;
    if (vehicleId) {
      url = this.appendInventoryVehicleFilter(url, vehicleId);
    }
    return this.cachedGetWithAuth(url);
    // Optimized: Selective population for paginated inventory
  }

  public getInventoryBrowseItems(
    page: number = 1,
    pageSize: number = 6,
    filters: { vehicleIds?: Array<string | number>; baseColorId?: string | number } = {}
  ): Observable<object> {
    let url = `${this.strapUrl}/api/inventory-items` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats&populate[image][fields][2]=width&populate[image][fields][3]=height` +
      `&populate[gallery][fields][0]=url&populate[gallery][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId&populate[vehicle][fields][2]=title` +
      `&populate[vehicle][fields][3]=price&populate[vehicle][fields][4]=isSubVehicle` +
      `&populate[base_color][fields][0]=name` +
      `&populate[dealer][fields][0]=name&populate[dealer][fields][1]=address&populate[dealer][fields][2]=phone&populate[dealer][fields][3]=website` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=stripeColor&fields[3]=options&fields[4]=price&fields[5]=sequence&fields[6]=documentId` +
      `&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=sequence:asc`;

    const vehicleIds = (filters.vehicleIds || []).filter(id => id !== null && id !== undefined && id !== '');
    if (vehicleIds.length === 1) {
      url = this.appendInventoryVehicleFilter(url, String(vehicleIds[0]));
    } else if (vehicleIds.length > 1) {
      url = this.appendInventoryVehicleIdsFilter(url, vehicleIds.map(id => String(id)));
    }

    if (filters.baseColorId) {
      url += `&filters[base_color][id][$eq]=${encodeURIComponent(String(filters.baseColorId))}`;
    }

    return this.cachedGetWithAuth(url);
  }

  public getFilteredInventoryItems(
    page: number = 1,
    pageSize: number = 6,
    vehicleId?: string,
    state?: string,
    city?: string,
    color?: string,
    paintOption?: string
  ): Observable<object> {
    let url = `${this.strapUrl}/api/inventory-items` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name` +
      `&populate[base_color][fields][0]=name` +
      `&populate[dealer][populate][state][fields][0]=name` +
      `&populate[dealer][populate][city][fields][0]=name` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=price&fields[3]=sequence` +
      `&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=sequence:asc`;
    // Optimized: Selective population for filtered inventory queries

    if (vehicleId) {
      url = this.appendInventoryVehicleFilter(url, vehicleId);
    }

    if (state) {
      // Filter by dealer's state - try different fields for better matching
      url += `&filters[dealer][state][name][$eq]=${encodeURIComponent(state)}`;
    }

    if (city) {
      // Filter by dealer's city name directly
      url += `&filters[dealer][city][name][$eq]=${encodeURIComponent(city)}`;
    }

    if (color) {
      // Color filtering
      url += `&filters[base_color][name][$eq]=${encodeURIComponent(color)}`;
    }

    if (paintOption) {
      // Paint option filtering using the paint_option relationship
      url += `&filters[paint_option][name][$eq]=${encodeURIComponent(paintOption)}`;
    }

    return this.cachedGetWithAuth(url);
  }

  public getInventoryItemById(id: string): Observable<object> {
    return this.cachedGetWithAuth(
      `${this.strapUrl}/api/inventory-items?filters[id][$eq]=${id}` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=stripeColor&fields[3]=options&fields[4]=price` +
      `&populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[gallery][fields][0]=url&populate[gallery][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId` +
      `&populate[dealer][fields][0]=name&populate[dealer][fields][1]=address&populate[dealer][fields][2]=phone&populate[dealer][fields][3]=website` +
      `&populate[dealer][populate][logo][fields][0]=url&populate[dealer][populate][logo][fields][1]=formats` +
      `&populate[base_color][fields][0]=name`
    );
  }

  public getInventoryItemByChassis(chassis: string): Observable<object> {
    return this.cachedGetWithAuth(
      `${this.strapUrl}/api/inventory-items?filters[chassis][$eq]=${encodeURIComponent(chassis)}` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=stripeColor&fields[3]=options&fields[4]=price&fields[5]=inventoryStatus&fields[6]=priceNote&fields[7]=specDisclaimer` +
      `&populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[gallery][fields][0]=url&populate[gallery][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId&populate[vehicle][fields][2]=title` +
      `&populate[dealer][fields][0]=name&populate[dealer][fields][1]=address&populate[dealer][fields][2]=phone&populate[dealer][fields][3]=website` +
      `&populate[dealer][populate][logo][fields][0]=url&populate[dealer][populate][logo][fields][1]=formats` +
      `&populate[dealer][populate][city][fields][0]=name` +
      `&populate[dealer][populate][state][fields][0]=abbreviation&populate[dealer][populate][state][fields][1]=name` +
      `&populate[base_color][fields][0]=name`
    );
  }

  public getFeaturedInventoryItems(): Observable<object> {
    return this.cachedGetWithAuth(
      `${this.strapUrl}/api/inventory-items` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId&populate[vehicle][fields][2]=title` +
      `&populate[base_color][fields][0]=name&populate[dealer][fields][0]=name` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=stripeColor&fields[3]=options&fields[4]=price&fields[5]=sequence&fields[6]=documentId`
    );
  }

  public getAvailableInventoryItems(): Observable<object> {
    return this.cachedGetWithAuth(
      `${this.strapUrl}/api/inventory-items` +
      `?populate[image][fields][0]=url&populate[image][fields][1]=formats` +
      `&populate[vehicle][fields][0]=name&populate[vehicle][fields][1]=documentId&populate[vehicle][fields][2]=title` +
      `&populate[base_color][fields][0]=name&populate[dealer][fields][0]=name` +
      `&fields[0]=chassis&fields[1]=color&fields[2]=stripeColor&fields[3]=options&fields[4]=price&fields[5]=sequence&fields[6]=documentId`
    );
  }

  public updateInventoryItem(itemId: string, itemData: any): Observable<object> {
    const url = `${this.strapUrl}/api/inventory-items?filters[documentId][$eq]=${itemId}`;
    return this._http.put(url, { data: itemData }, { headers: this.getHeaders() });
  }

  // News & Events endpoints - no authentication needed
  public getNewsItems(page: number = 1, pageSize: number = 4): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/news-items` +
      `?fields[0]=title&fields[1]=description&fields[2]=publishedDate&fields[3]=author&fields[4]=id&fields[5]=readTime` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats&populate[coverImage][fields][2]=alternativeText` +
      `&sort=publishedDate:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`
    );
  }

  public searchNewsItems(searchQuery: string, page: number = 1, pageSize: number = 4): Observable<object> {
    const encodedQuery = encodeURIComponent(searchQuery);
    return this._http.get(
      `${this.strapUrl}/api/news-items` +
      `?fields[0]=title&fields[1]=description&fields[2]=publishedDate&fields[3]=author&fields[4]=id&fields[5]=readTime` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats&populate[coverImage][fields][2]=alternativeText` +
      `&sort=publishedDate:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}` +
      `&filters[$or][0][title][$containsi]=${encodedQuery}&filters[$or][1][description][$containsi]=${encodedQuery}`
    );
  }

  public getNewsItemById(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/news-items?filters[id][$eq]=${id}&populate=*`);
  }

  public getEvents(page: number = 1, pageSize: number = 6): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/events` +
      `?fields[0]=title&fields[1]=scheduledDate&fields[2]=author&fields[3]=id` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats&populate[coverImage][fields][2]=alternativeText` +
      `&sort=scheduledDate:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`
    );
  }

  public searchEvents(searchQuery: string, page: number = 1, pageSize: number = 6): Observable<object> {
    const encodedQuery = encodeURIComponent(searchQuery);
    return this._http.get(
      `${this.strapUrl}/api/events` +
      `?fields[0]=title&fields[1]=scheduledDate&fields[2]=author&fields[3]=id` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats&populate[coverImage][fields][2]=alternativeText` +
      `&sort=scheduledDate:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}` +
      `&filters[$or][0][title][$containsi]=${encodedQuery}`
    );
  }

  public getEventById(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/events?filters[id][$eq]=${id}&populate=*`);
  }

  public getFeaturedNews(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/news-items` +
      `?filters[IsTop][$eq]=true` +
      `&fields[0]=title&fields[1]=description&fields[2]=publishedDate&fields[3]=author&fields[4]=readTime` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats` +
      `&sort=publishedDate:desc`
    );
  }

  public getFeaturedEvents(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/events` +
      `?fields[0]=title&fields[1]=scheduledDate&fields[2]=author` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats` +
      `&sort=scheduledDate:desc&pagination[pageSize]=6`
    );
  }

  public updateNewsItem(newsId: string, newsData: any): Observable<object> {
    const url = `${this.strapUrl}/api/news-items?filters[documentId][$eq]=${newsId}`;
    return this._http.put(url, { data: newsData }, { headers: this.getHeaders() });
  }

  public updateEvent(eventId: string, eventData: any): Observable<object> {
    const url = `${this.strapUrl}/api/events?filters[documentId][$eq]=${eventId}`;
    return this._http.put(url, { data: eventData }, { headers: this.getHeaders() });
  }


  public clearCache(): void {
    // Clearing Strapi cache
    this._cache.clear();
  }

  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this._cache.size,
      keys: Array.from(this._cache.keys())
    };
  }

  // Homepage video endpoint - no authentication needed


  // Home video endpoint - no authentication needed
  public getHomeVideo(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/home-video?populate[video][fields][0]=url`).pipe(
      tap((response: any) => {
        // Cache the video URL for synchronous access
        if (response?.data?.video?.url) {
          this._cachedHomeVideoUrl = this.getFullImageUrl(response.data.video.url);
        }
      })
    );
  }

  public getHomeVideoLicenseSection(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/home-video-license-section` +
      `?fields[0]=title&fields[1]=description` +
      `&populate[images][fields][0]=url&populate[images][fields][1]=formats&populate[images][fields][2]=alternativeText`
    );
  }

  public getHomeVideoMain(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/home-video-main` +
      `?fields[0]=title&fields[1]=description` +
      `&populate[countryFlagImage][fields][0]=url&populate[countryFlagImage][fields][1]=formats`
    );
  }

  /**
   * Site header copy (logo slogan, dealer hotline).
   * Primary source: Strapi single type `site-header`.
   * Fallback: optional `slogan` / `dealerPhone` on `home-video-main`, then defaults.
   */
  public getSiteHeaderSettings(): Observable<SiteHeaderSettings> {
    const defaults: SiteHeaderSettings = {
      slogan: 'ICONIC PERFORMANCE',
      dealerPhone: '(800) 297-6253',
      dealerPhoneHref: 'tel:+18002976253',
    };

    return this.cachedGetWithAuth<{ data?: Partial<SiteHeaderSettings> }>(
      `${this.strapUrl}/api/site-header?fields[0]=slogan&fields[1]=dealerPhone&fields[2]=dealerPhoneHref`,
      5 * 60 * 1000,
      true
    ).pipe(
      switchMap((siteHeaderRes) => {
        if (siteHeaderRes?.data) {
          return of(this.mapSiteHeaderSettings(siteHeaderRes.data, defaults));
        }

        return this.getHomeVideoMain().pipe(
          map((mainRes: any) => {
            const data = mainRes?.data;
            if (data?.slogan || data?.dealerPhone || data?.dealerPhoneHref) {
              return this.mapSiteHeaderSettings(data, defaults);
            }
            return defaults;
          }),
          catchError(() => of(defaults))
        );
      }),
      catchError(() => of(defaults))
    );
  }

  private mapSiteHeaderSettings(
    data: Partial<SiteHeaderSettings> | null | undefined,
    defaults: SiteHeaderSettings
  ): SiteHeaderSettings {
    const phone = (data?.dealerPhone || '').trim() || defaults.dealerPhone;
    return {
      slogan: (data?.slogan || '').trim() || defaults.slogan,
      dealerPhone: phone,
      dealerPhoneHref: (data?.dealerPhoneHref || '').trim() || this.toTelHref(phone),
    };
  }

  private toTelHref(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (!digits) {
      return 'tel:+18002976253';
    }
    return digits.startsWith('1') ? `tel:+${digits}` : `tel:+1${digits}`;
  }

  // Privacy policy endpoint - no authentication needed
  public getPrivacyPolicy(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/privacy-policy?fields[0]=content`);
  }

  // Testimonials endpoints - no authentication needed
  public getTestimonials(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/testimonials` +
      `?fields[0]=name&fields[1]=title&fields[2]=content` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats` +
      `&sort=createdAt:desc`
    );
  }

  public getTestimonialsPaginated(page: number = 1, pageSize: number = 6): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/testimonials` +
      `?fields[0]=name&fields[1]=title&fields[2]=content` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats` +
      `&sort=createdAt:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`
    );
  }

  public getTestimonialById(id: string): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/testimonials?filters[id][$eq]=${id}&populate=*`);
  }

  public updateTestimonial(testimonialId: string, testimonialData: any): Observable<object> {
    const url = `${this.strapUrl}/api/testimonials?filters[documentId][$eq]=${testimonialId}`;
    return this._http.put(url, { data: testimonialData }, { headers: this.getHeaders() });
  }

  // Heritage page endpoint - no authentication needed
  public getHeritagePage(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/heritage-page?populate[facts][populate][0]=image`);
  }

  // Process page endpoint - no authentication needed
  public getProcessPage(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/process-page` +
      `?fields[0]=subtitle&fields[1]=title&fields[2]=description` +
      `&populate[steps][fields][0]=step&populate[steps][fields][1]=title&populate[steps][fields][2]=description`
    );
  }

  // Tradition page endpoint - no authentication needed
  public getTraditionPage(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/tradition-page` +
      `?fields[0]=subtitle&fields[1]=title&fields[2]=description&fields[3]=list` +
      `&populate[coverImage][fields][0]=url&populate[coverImage][fields][1]=formats`
    );
  }

  // About-us page endpoint - no authentication needed
  public getAboutUs(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/about-us` +
      `?populate[bannerImage][fields][0]=url&populate[bannerImage][fields][1]=formats` +
      `&populate[historyImage][fields][0]=url&populate[historyImage][fields][1]=formats` +
      `&populate[factoryImage][fields][0]=url&populate[factoryImage][fields][1]=formats` +
      `&populate[vintageImages][fields][0]=url&populate[vintageImages][fields][1]=formats`
    );
  }

  // Factory page endpoint - no authentication needed
  public getFactory(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/factory` +
      `?populate[facts][fields][0]=title&populate[facts][fields][1]=description` +
      `&populate[facts][populate][image][fields][0]=url&populate[facts][populate][image][fields][1]=formats` +
      `&populate[gallery][fields][0]=url&populate[gallery][fields][1]=formats&populate[gallery][fields][2]=alternativeText`
    );
  }

  // Location endpoints - no authentication needed
  public getStates(): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/states?sort=name:asc`);
  }

  public getCitiesByState(stateId: number): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/cities?filters[state][id][$eq]=${stateId}&fields[0]=id&fields[1]=name`);
  }

  public getCitiesByStateName(stateName: string): Observable<object> {
    // First get all states, find the matching one, then get cities by state ID
    return new Observable<object>(observer => {
      this.cachedGetWithAuth(`${this.strapUrl}/api/states`).subscribe({
        next: (statesResponse: any) => {
          const states = statesResponse.data || [];
          const matchingState = states.find((state: any) =>
            state.name === stateName ||
            state.code === stateName ||
            state.abbreviation === stateName
          );

          if (matchingState) {
            // Now get cities by the found state ID
            this.cachedGetWithAuth(`${this.strapUrl}/api/cities?filters[state][id][$eq]=${matchingState.id}&fields[0]=id&fields[1]=name`).subscribe({
              next: (citiesResponse: any) => observer.next(citiesResponse),
              error: (error) => observer.error(error),
              complete: () => observer.complete()
            });
          } else {
            // No matching state found, return empty result
            observer.next({ data: [] });
            observer.complete();
          }
        },
        error: (error) => observer.error(error)
      });
    });
  }

  public getCities(): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/cities?fields[0]=id&fields[1]=name`);
  }

  public getBaseColors(): Observable<object> {
    return this.cachedGetWithAuth(
      `${this.strapUrl}/api/base-colors` +
      `?populate[soldOutFallback][populate]=*` +
      `&sort=name:asc`
    );
  }

  private appendInventoryVehicleFilter(url: string, vehicleId: string): string {
    return (
      url +
      `&filters[$or][0][vehicle][id][$eq]=${vehicleId}` +
      `&filters[$or][1][inventoryCategories][id][$eq]=${vehicleId}`
    );
  }

  private appendInventoryVehicleIdsFilter(url: string, vehicleIds: string[]): string {
    vehicleIds.forEach((id, index) => {
      url +=
        `&filters[$or][${index * 2}][vehicle][id][$eq]=${encodeURIComponent(id)}` +
        `&filters[$or][${index * 2 + 1}][inventoryCategories][id][$eq]=${encodeURIComponent(id)}`;
    });
    return url;
  }

  public getRandomImageBySubvehicle(subvehicleId: string): Observable<object> {
    // Check localStorage first (20-minute cache - random selection is intentionally cached for consistency)
    const cacheKey = `random-img-subvehicle-${subvehicleId}`;
    const cached = this.getFromLocalStorageCache(cacheKey, 20 * 60 * 1000); // 20 minutes

    if (cached) {
      return of(cached);
    }

    // Fetch from API with 20-minute TTL (cached random selection provides consistency)
    const apiCall$ = this.cachedGetWithAuth<object>(
      `${this.strapUrl}/api/inventory-items/subvehicle/${subvehicleId}/random-image`,
      20 * 60 * 1000, // 20 minutes
      true
    ).pipe(
      tap(result => {
        // Store in localStorage for persistence across navigation
        this.setLocalStorageCache(cacheKey, result, 20 * 60 * 1000);
      })
    );

    // Use TransferState for SSR hydration
    const key = `strapi-random-image-subvehicle-${subvehicleId}`;
    return this._transferState.useTransferState(key, () => apiCall$);
  }

  public getRandomImageByVehicle(vehicleId: string): Observable<object> {
    // Check localStorage first (20-minute cache - random selection is intentionally cached for consistency)
    const cacheKey = `random-img-vehicle-${vehicleId}`;
    const cached = this.getFromLocalStorageCache(cacheKey, 20 * 60 * 1000); // 20 minutes

    if (cached) {
      return of(cached);
    }

    // Fetch from API with 20-minute TTL (cached random selection provides consistency)
    const apiCall$ = this.cachedGetWithAuth<object>(
      `${this.strapUrl}/api/inventory-items/vehicle/${vehicleId}/random-image`,
      20 * 60 * 1000, // 20 minutes
      true
    ).pipe(
      tap(result => {
        // Store in localStorage for persistence
        this.setLocalStorageCache(cacheKey, result, 20 * 60 * 1000);
      })
    );

    const key = `strapi-random-image-${vehicleId}`;
    return this._transferState.useTransferState(key, () => apiCall$);
  }

  // Paint swatch endpoints - no authentication needed
  public getPaintSwatchByVehicleAndPaint(vehicleId: string, paintOptionId: string): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/paint-swatches?filters[vehicle][id][$eq]=${vehicleId}&filters[paint_option][id][$eq]=${paintOptionId}` +
      `&populate[images][fields][0]=id&populate[images][fields][1]=url&populate[images][fields][2]=formats&populate[images][fields][3]=alternativeText`
    );
  }

  // Paint options endpoints - no authentication needed
  public getPaintOptions(): Observable<object> {
    return this.cachedGetWithAuth(`${this.strapUrl}/api/paint-options?pagination[pageSize]=100`);
  }

  // All-heading endpoint - no authentication needed
  public getAllHeadings(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/all-heading?populate[headings][populate][dropdownValues]populate=*`);
  }

  // Get Default Sub Vehicle Image
  public getDefaultSubvehicleImage(vehicleId: string): Observable<object> {
    // Check localStorage first (20-minute cache for default images)
    const cacheKey = `default-subvehicle-img-${vehicleId}`;
    const cached = this.getFromLocalStorageCache(cacheKey, 20 * 60 * 1000); // 20 minutes

    if (cached) {
      return of(cached);
    }

    // Fetch from API with 20-minute TTL (default images don't change often)
    const apiCall$ = this.cachedGetWithAuth<object>(
      `${this.strapUrl}/api/inventory-items/vehicle/${vehicleId}/default-subvehicle-image`,
      20 * 60 * 1000, // 20 minutes
      true
    ).pipe(
      tap(result => {
        // Store in localStorage for persistence across navigation
        this.setLocalStorageCache(cacheKey, result, 20 * 60 * 1000);
      })
    );

    // Use TransferState for SSR hydration
    const key = `strapi-default-subvehicle-image-${vehicleId}`;
    return this._transferState.useTransferState(key, () => apiCall$);
  }

  // Get FAQ Categories
  public getFAQCategories(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/faq-categories` +
      `?fields[0]=name&fields[1]=order&fields[2]=isSubCategory` +
      `&sort=order:asc`
    );
  }


  // Get About Us Middle Content
  // public getAboutUsMiddleContent(): Observable<object> {
  //   return this.cachedGet(`${this.strapUrl}/api/about-us-middle?populate[galleryItems][populate]=galleryItemImage`);
  // }
  // Get License Images
  public getLicenseSection(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/license-section?populate=licenseImages`
    );
  }


  // Get About Us Middle Content
  public getAboutUsMiddleContent(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/about-us-middle?populate[galleryItems][populate]=galleryItemImage`);
  }

  // Get License page endpoint - no authentication needed (single type: license-section)
  public getLicense(): Observable<object> {
    return this.cachedGet(
      `${this.strapUrl}/api/license-section` +
      `?fields[0]=licenseSubHeading&fields[1]=licenseDescriptionTop&fields[2]=licenseDescriptionMiddle&fields[3]=licenseDescriptionBottom&fields[4]=title` +
      `&populate[licenseImages][fields][0]=url&populate[licenseImages][fields][1]=formats&populate[licenseImages][fields][2]=alternativeText`
    );
  }

  // Get License Gallery Top collection - no authentication needed
  public getLicenseGalleryTop(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/license-gallery-tops?populate[galleryItems][populate]=galleryItemImage`);
  }

  // Get License Gallery Bottom collection - no authentication needed
  public getLicenseGalleryBottom(): Observable<object> {
    return this.cachedGet(`${this.strapUrl}/api/license-gallery-bottoms?populate[galleryItems][populate]=galleryItemImage`);
  }

  /**
   * LocalStorage Cache Helper Methods
   * Provides persistent caching across page navigations
   */

  private getFromLocalStorageCache(key: string, ttl: number): any | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const { data, timestamp } = JSON.parse(item);
      const age = Date.now() - timestamp;

      if (age > ttl) {
        localStorage.removeItem(key);
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  private setLocalStorageCache(key: string, data: any, ttl: number): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const item = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch {
      // Storage quota exceeded or other error - ignore
    }
  }
}
